import React, { useCallback, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import Switch from '../../../../../molecules/Switch'
import { useCreateQueryBlueprint } from '../../../../../../api/denali'
import { decipherError } from '../../../../../../utils/decipherError'

const useSubmitter = (form, onComplete) => {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { handleSubmit } = form
  const { mutateAsync: createQueryBlueprint } = useCreateQueryBlueprint()

  const onSubmit = useCallback(async (formData) => {
    const command = {
      code: formData.code,
      name: formData.name,
      description: formData.description || null,
      enabled: formData.enabled
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await createQueryBlueprint(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while creating the query blueprint')
        return
      }
      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [createQueryBlueprint, onComplete])

  const submitter = useCallback(async (e) => {
    const onValid = async (form) => {
      await onSubmit(form)
    }
    const onInvalid = (errors) => {
      console.error(errors)
    }

    const handler = handleSubmit(onValid, onInvalid)
    await handler(e)
  }, [handleSubmit, onSubmit])

  return {
    submitter,
    processing,
    error
  }
}

function CreateQueryBlueprintForm ({ onCancel, onComplete }) {
  const form = useForm({
    defaultValues: {
      code: '',
      name: '',
      description: '',
      enabled: false
    },
    mode: 'onChange'
  })

  const { submitter, processing, error } = useSubmitter(form, onComplete)

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Controller
          control={form.control}
          name='code'
          rules={{
            required: { value: true, message: 'Required' },
            maxLength: { value: 64, message: 'Maximum 64 Characters' }
          }}
          render={(f) => (
            <SydLabel required label='Code' error={hookFormErrorAdapter(form, f.fieldState)}>
              <SydInput {...f.field} />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          control={form.control}
          name='name'
          rules={{
            required: { value: true, message: 'Required' },
            maxLength: { value: 128, message: 'Maximum 128 Characters' }
          }}
          render={(f) => (
            <SydLabel required label='Name' error={hookFormErrorAdapter(form, f.fieldState)}>
              <SydInput {...f.field} />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={form.control}
          name='description'
          render={(f) => (
            <SydLabel label='Description'>
              <SydInput {...f.field} multiline rows={3} />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          control={form.control}
          name='enabled'
          render={(f) => (
            <SydLabel label='Enabled'>
              <Switch
                checked={f.field.value}
                onChange={(e) => f.field.onChange(e.target.checked)}
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <SydModalActions>
          {error ? <div className='__error'>{error}</div> : null}
          <SydButton disabled={processing} variant='ghost' size='md' onClick={onCancel}>
            Cancel
          </SydButton>
          <SydButton
            processing={processing}
            disabled={!form.formState.isValid}
            variant='primary'
            size='md'
            onClick={submitter}
          >
            Create
          </SydButton>
        </SydModalActions>
      </Grid>
    </Grid>
  )
}

CreateQueryBlueprintForm.propTypes = {
  onComplete: PropTypes.func,
  onCancel: PropTypes.func
}

export default CreateQueryBlueprintForm
